import {
  DBB_CODE_TYPE,
  DBB_CONTRACT_TYPE,
  DBB_CUSTOMER_TYPE,
  DBB_TYRE_POSITION,
  DBB_TYRE_SET_TYPE,
  DBB_TYRE_STATE,
  DBB_TYRE_TYPE,
  DBB_WHEEL_TYPE,
  SEASONS,
} from "./../../../../modules/Appointments/common";

export const DBB_MANDATORY_CUSTOMER_FIELDS = ["street", "number", "postcode", "place"];

export const DBB_MANDATORY_CAR_FIELDS = ["reg_nr", "vin_nr", "make", "model", "mileage"];

export const MINIMUM_COMPLETE_TYRE_SETS = 2;

export const DEFAULT_PROFILE = 10;

const TYRE_TYPES = {
  [SEASONS.WINTER]: DBB_TYRE_TYPE.WINTER,
  [SEASONS.SUMMER]: DBB_TYRE_TYPE.SUMMER,
  [SEASONS.ALL_SEASONS]: DBB_TYRE_TYPE.ALL_SEASON,
  [SEASONS.NO_SEASON]: DBB_TYRE_TYPE.ALL_SEASON,
  [SEASONS.NOT_AVAILABLE]: DBB_TYRE_TYPE.ALL_SEASON,
};

const CUSTOMER_TYPES_BY_PARTY = {
  driver: DBB_CUSTOMER_TYPE.DRIVER,
  owner: DBB_CUSTOMER_TYPE.OWNER,
};

const CONTRACT_SECTIONS = {
  driver: { customerField: "driver", unknownField: "is_driver_unknown", label: "driver", defaultText: "Driver" },
  owner: { customerField: "owner", unknownField: "is_owner_unknown", label: "owner", defaultText: "Owner" },
};

export const TYRE_SET_TYPES = [
  { setType: DBB_TYRE_SET_TYPE.ON_CAR, label: "dbb_on_car", defaultText: "On Car" },
  { setType: DBB_TYRE_SET_TYPE.STORAGE, label: "storage", defaultText: "Storage" },
];

export const TYRE_SET_POSITIONS = [
  { position: DBB_TYRE_POSITION.FRONT_LEFT, label: "front_left", defaultText: "Front Left" },
  { position: DBB_TYRE_POSITION.FRONT_RIGHT, label: "front_right", defaultText: "Front Right" },
  { position: DBB_TYRE_POSITION.REAR_LEFT, label: "rear_left", defaultText: "Rear Left" },
  { position: DBB_TYRE_POSITION.REAR_RIGHT, label: "rear_right", defaultText: "Rear Right" },
];

const SET_STATES = {
  [DBB_TYRE_SET_TYPE.ON_CAR]: DBB_TYRE_STATE.ON_CAR,
  [DBB_TYRE_SET_TYPE.STORAGE]: DBB_TYRE_STATE.ON_STORAGE,
};

const CAR_TYRE_FIELDS = [
  { position: DBB_TYRE_POSITION.FRONT_LEFT, tyreField: "front_left_tyre", profileField: "fl_profile" },
  { position: DBB_TYRE_POSITION.FRONT_RIGHT, tyreField: "front_right_tyre", profileField: "fr_profile" },
  { position: DBB_TYRE_POSITION.REAR_LEFT, tyreField: "rear_left_tyre", profileField: "rl_profile" },
  { position: DBB_TYRE_POSITION.REAR_RIGHT, tyreField: "rear_right_tyre", profileField: "rr_profile" },
];

export const getCustomerOrCompanyName = customer => {
  if (!customer) return "";

  const name = `${customer.firstname || ""} ${customer.surname || ""}`.trim();

  return name || customer.company || "";
};

export const toContractCustomer = (customer, role) => {
  if (!customer) return undefined;

  return { ...customer, role, name: getCustomerOrCompanyName(customer), number: customer.house_nr || "" };
};

export const getContractSections = contractType => {
  if (contractType === DBB_CONTRACT_TYPE.LEASE) return [CONTRACT_SECTIONS.driver];
  if (contractType === DBB_CONTRACT_TYPE.FLEET) return [CONTRACT_SECTIONS.owner, CONTRACT_SECTIONS.driver];

  return [CONTRACT_SECTIONS.owner];
};

export const hasContractCustomerName = customer => !!(customer?.company?.trim() || customer?.name?.trim());

export const getIncompleteCustomerSections = values =>
  getContractSections(values.contract_type).reduce((customerFields, { customerField, unknownField }) => {
    if (values[unknownField]) return customerFields;

    const customer = values[customerField];
    const hasMissingField = DBB_MANDATORY_CUSTOMER_FIELDS.some(field => !customer?.[field]) || !hasContractCustomerName(customer);

    if (hasMissingField) customerFields.push(customerField);

    return customerFields;
  }, []);

export const isCarFieldMissing = value => (typeof value === "number" ? value <= 0 : !value);

const getCarTyres = (car, setType) => (setType === DBB_TYRE_SET_TYPE.ON_CAR ? car.on_car_tyres : car.in_storage_tyres);

const toSetTyres = carTyres => {
  if (!carTyres) return [];

  return CAR_TYRE_FIELDS.reduce((setTyres, { position, tyreField, profileField }) => {
    const tyre = carTyres[tyreField];
    if (tyre) setTyres.push({ tyre, position, profile: carTyres[profileField] });

    return setTyres;
  }, []);
};

export const getDefaultTyreSets = car => TYRE_SET_TYPES.map(({ setType }) => ({ set_type: setType, wheel_type: null, tyres: toSetTyres(getCarTyres(car, setType)) }));

export const countCompleteTyreSets = (tyreSets = []) =>
  tyreSets.filter(({ wheel_type, tyres }) => wheel_type && TYRE_SET_POSITIONS.every(({ position }) => tyres.some(tyre => tyre.position === position))).length;

export const getContractFormErrors = values => ({
  hasIncompleteCar: DBB_MANDATORY_CAR_FIELDS.some(field => isCarFieldMissing(values.car[field])),
  hasIncompleteCustomer: !!getIncompleteCustomerSections(values).length,
  hasMissingLeaseCompany: values.contract_type === DBB_CONTRACT_TYPE.LEASE && !values.lease_company_id,
  hasMissingStorageLocation: values.pickup_by_dbb && !values.storage_location_id,
  hasIncompleteTyreSets: countCompleteTyreSets(values.tyre_sets) < MINIMUM_COMPLETE_TYRE_SETS,
});

export const getWheelTypeOptions = t => [
  { key: DBB_WHEEL_TYPE.NONE, value: DBB_WHEEL_TYPE.NONE, text: t("none").message || "None" },
  { key: DBB_WHEEL_TYPE.STEEL, value: DBB_WHEEL_TYPE.STEEL, text: t("steel").message || "Steel" },
  { key: DBB_WHEEL_TYPE.ALLOY, value: DBB_WHEEL_TYPE.ALLOY, text: t("alloy").message || "Alloy" },
];

export const getContractTypeOptions = t => [
  { key: DBB_CONTRACT_TYPE.PRIVATE, value: DBB_CONTRACT_TYPE.PRIVATE, text: t("private").message || "Private" },
  { key: DBB_CONTRACT_TYPE.LEASE, value: DBB_CONTRACT_TYPE.LEASE, text: t("lease").message || "Lease" },
  { key: DBB_CONTRACT_TYPE.FLEET, value: DBB_CONTRACT_TYPE.FLEET, text: t("fleet").message || "Fleet" },
];

const toContractCode = (value, type) => (value ? [{ Value: value, Type: type }] : []);

const STORAGE_BOUND_TYRE = { LocationName: "", SubLocationName: "", LocationToken: "" };

const toCreateContractTyre = (wheelType, state, storage, { tyre, position, profile }) => ({
  Code: toContractCode(tyre.ean || "", DBB_CODE_TYPE.MANUFACTURER),
  Storage: storage,
  Position: position,
  State: state,
  Brand: tyre.manufacturer,
  Description: [{ Language: "NL", Value: tyre.description }],
  TyreType: TYRE_TYPES[tyre.season] || DBB_TYRE_TYPE.ALL_SEASON,
  Size: tyre.size,
  Width: tyre.width,
  Height: tyre.height,
  SpeedIndicator: tyre.speed,
  LoadIndex: tyre.carry_capacity,
  WheelType: wheelType,
  MeasuredDepth: profile,
});

const toCreateContractCustomer = (customerField, customer) => ({
  CustomerType: CUSTOMER_TYPES_BY_PARTY[customerField],
  Initials: customer.initials || "",
  Name: customer.name?.trim() || customer.company?.trim() || "",
  Street: customer.street || "",
  Number: customer.number,
  Postalcode: customer.postcode || "",
  City: customer.place || "",
  Sex: "U",
  Code: toContractCode(customer.dms_nr, DBB_CODE_TYPE.LOCAL),
});

export const toCreateContractPayload = (car, values) => {
  const payload = {
    car_id: car.id,
    contract_type: values.contract_type,
    pickup_by_dbb: values.pickup_by_dbb,
    car: {
      License: values.car.reg_nr,
      VIN: values.car.vin_nr,
      Brand: values.car.make,
      Model: values.car.model,
      ModelYear: values.car.model_year,
      Mileage: values.car.mileage,
    },
    tyres: values.tyre_sets.flatMap(({ set_type, wheel_type, tyres }) => {
      const storage = set_type === DBB_TYRE_SET_TYPE.STORAGE ? STORAGE_BOUND_TYRE : undefined;

      return tyres.map(setTyre => toCreateContractTyre(wheel_type, SET_STATES[set_type], storage, setTyre));
    }),
  };

  if (values.contract_type === DBB_CONTRACT_TYPE.LEASE && values.lease_company_id) payload.lease_company_id = values.lease_company_id;
  if (values.pickup_by_dbb && values.storage_location_id) payload.storage_location_id = values.storage_location_id;

  getContractSections(values.contract_type).forEach(({ customerField, unknownField }) => {
    const customer = values[customerField];
    if (values[unknownField] || !customer) return;

    payload[customerField] = toCreateContractCustomer(customerField, customer);
  });

  return payload;
};
